import React, { useRef, useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faStopwatch, faClipboardList, faGripVertical, faArrowTurnUp, faTruckArrowRight, faRoute, faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { faEye } from '@fortawesome/free-regular-svg-icons';
import { useAlert } from '../../providers/AlertProvider';
import RouteOptimiseModal from './RouteOptimiseModal';
import { optimiseRoute, applyRouteOrder } from '../../api/route';
import { writeRouteDragData, readRouteDragData, allowRouteDrop } from '../../utils/routeDragDrop';
import logger from '../../utils/logger';

/**
 * AllocatedTable — single table for all driver groups.
 * Each driver is a <tbody> with a header row, visually bordered.
 * Drag rows within a driver to reorder; drag onto another driver's section to reassign.
 */
const AllocatedTable = ({
    driverGroups,
    onReorder,
    onReassign,
    onExternalDrop,
    onRemoveDriver,
    theme,
    isLocked,
    showCostings,
    completedRouteUuids,
    onViewCompletion,
    deliveryAlerts,
    showJobComments,
    showJobCostings,
    showStartTime,
    showDemurrage,
    showDeliveryDetails,
    showAddFromDropdown,
    toggleConfirmed,
    onConfirmAllForDriver,
    onUnconfirmAllForDriver,
    contractRoute,
    editSubcontract,
    canEdit,
    onPodOff,
    deleteRoute,
    moveRoute,
    removeVehicle,
    vehicleServiceList,
    vehicleOverdueList,
    vorList,
    showCustomerConsignment,
    showNetworkFreightMetrics,
}) => {
    const { showAlert } = useAlert();
    const [dragOverKey, setDragOverKey] = useState(null);
    const [isReordering, setIsReordering] = useState(false);
    const reorderInProgress = useRef(false);
    const [podContextMenu, setPodContextMenu] = useState(null); // { x, y, route_uuid }
    const [moveUuid, setMoveUuid] = useState(null);
    const [moveDate, setMoveDate] = useState('');
    const [optimiseModal, setOptimiseModal] = useState({ open: false, result: null, driverName: '', loading: false });
    const vans = ['FE24_AYJ', 'FE24_AYK'];

    // Close context menu on any outside click
    useEffect(() => {
        if (!podContextMenu) return;
        const close = () => setPodContextMenu(null);
        window.addEventListener('click', close);
        window.addEventListener('contextmenu', close);
        return () => {
            window.removeEventListener('click', close);
            window.removeEventListener('contextmenu', close);
        };
    }, [podContextMenu]);

    const handleToggleConfirmed = (route_uuid, confirmed, is_subbed, groupRoutes) => {
        showAlert({
            message: 'Change Status?',
            type: 'success',
            buttons: is_subbed ? [
                { type: 'primary',   label: 'Unsub',  onClick: () => contractRoute(route_uuid, false) },
                { type: 'warning',   label: 'Edit',   onClick: () => editSubcontract(route_uuid) },
                ...(canEdit ? [{ type: 'info', label: 'Pod', onClick: () => onPodOff?.(route_uuid) }] : []),
                { type: 'secondary', label: 'Cancel' },
            ] : [
                ...(confirmed
                    ? [{ type: 'success', label: 'Unconfirm', onClick: () => onUnconfirmAllForDriver?.(groupRoutes) }]
                    : [{ type: 'success', label: 'Confirm',       onClick: () => toggleConfirmed(route_uuid, confirmed) }]
                ),
                { type: 'primary',   label: 'Sub',    onClick: () => contractRoute(route_uuid, true) },
                ...(canEdit ? [{ type: 'info', label: 'Pod', onClick: () => onPodOff?.(route_uuid) }] : []),
                { type: 'secondary', label: 'Cancel' },
            ],
        });
    };

    const formatTimeToHHMM = (t) => {
        if (!t) return null;
        return t.length <= 5 ? t : t.substring(0, 5);
    };

    const confirmDelete = (route_uuid) => {
        showAlert({
            message: 'What do you want to do with this job?',
            type: 'warning',
            buttons: [
                { label: 'Delete', type: 'danger', onClick: () => deleteRoute(route_uuid) },
                { label: 'Move', type: 'primary', onClick: () => { setMoveDate(''); setMoveUuid(route_uuid); } },
                { label: 'Cancel', type: 'secondary' },
            ],
        });
    };

    const handleOptimiseClick = (group) => {
        if (group.routes.length < 2) return;
        showAlert({
            message: `Optimise route for ${group.driverName}?`,
            type: 'primary',
            buttons: [
                {
                    label: 'Full',
                    type: 'primary',
                    description: 'Keep 1st drop fixed, optimise all remaining stops',
                    onClick: () => runOptimise(group, 'full'),
                },
                {
                    label: 'Partial',
                    type: 'info',
                    description: 'Keep 1st and last drop fixed, optimise stops in between',
                    onClick: () => runOptimise(group, 'partial'),
                },
                { label: 'Cancel', type: 'secondary' },
            ],
        });
    };

    const runOptimise = async (group, mode) => {
        const startTime = Date.now();
        setOptimiseModal({ open: false, result: null, driverName: group.driverName, loading: true });
        const stops = group.routes
            .sort((a, b) => (a.drop_number ?? 999) - (b.drop_number ?? 999))
            .map(r => ({ route_uuid: r.route_uuid, job: r.job, postcode: r.postcode }));
        const { status, data } = await optimiseRoute(stops, mode);

        // Enforce a minimum 10-second loading display; if done early wait the remainder,
        // if already over 10s check again after another 10s to keep the animation visible
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(12000 - elapsed, 0);
        await new Promise(resolve => setTimeout(resolve, remaining));

        if (status === 200 && data.optimised) {
            setOptimiseModal({ open: true, result: data.optimised, driverName: group.driverName, loading: false });
        } else {
            setOptimiseModal({ open: false, result: null, driverName: '', loading: false });
            showAlert({
                message: data?.error || 'Failed to optimise route',
                type: 'danger',
                buttons: [{ label: 'Close', type: 'danger' }],
            });
        }
    };

    const handleAcceptOptimise = async (optimised) => {
        const updates = optimised.map(s => ({ route_uuid: s.route_uuid, drop_number: s.new_drop_number }));
        const { status } = await applyRouteOrder(updates);
        if (status === 200 || status === 207) {
            // Update state via onReorder using the same shape
            onReorder(updates);
        } else {
            showAlert({ message: 'Failed to save new order', type: 'danger', buttons: [{ label: 'Close' }] });
        }
    };

    // ── Drag handlers ─────────────────────────────────────────────────────────

    const reorderWithinDriver = async (group, fromIndex, toIndex) => {
        if (isLocked || reorderInProgress.current || !onReorder
            || fromIndex < 0 || toIndex < 0 || fromIndex >= group.routes.length
            || toIndex >= group.routes.length || fromIndex === toIndex
            || group.routes[fromIndex].confirmed) return;
        reorderInProgress.current = true;
        setIsReordering(true);
        try {
            const reordered = [...group.routes];
            const [moved] = reordered.splice(fromIndex, 1);
            reordered.splice(toIndex, 0, moved);
            await onReorder(reordered.map((r, i) => ({ route_uuid: r.route_uuid, drop_number: i + 1 })));
        } catch (error) {
            logger.error('Drop order update failed:', error);
            showAlert({ message: 'Could not save the drop order. Please retry.', type: 'danger', buttons: [{ label: 'Close' }] });
        } finally {
            reorderInProgress.current = false;
            setIsReordering(false);
        }
    };

    const handleDragStart = (e, driverName, index, route) => {
        try {
            writeRouteDragData(e.dataTransfer, 'allocated_route', {
                route_uuid: route.route_uuid,
                fromDriver: driverName,
                route,
            });
        } catch (error) {
            e.preventDefault();
            logger.error('Route drag start failed:', error);
            showAlert({ message: 'Could not start dragging the route.', type: 'danger', buttons: [{ label: 'Close' }] });
        }
    };

    const handleDragEnd = () => {
        setDragOverKey(null);
    };

    const handleDropOnRow = async (e, targetDriverName, targetIndex, targetRoute) => {
        e.preventDefault();
        e.stopPropagation();
        if (isLocked) return;
        try {
            const { type, item } = readRouteDragData(e.dataTransfer);
            if (type !== 'allocated_route') {
                await onExternalDrop?.(e, targetRoute, targetIndex);
                return;
            }
            const { fromDriver, route } = item;
            if (fromDriver === targetDriverName) {
                const group = driverGroups.find(g => g.driverName === fromDriver);
                const fromIndex = group?.routes.findIndex(r => r.route_uuid === route?.route_uuid);
                if (!group || fromIndex < 0) throw new Error('Dragged route is no longer in this driver group.');
                await reorderWithinDriver(group, fromIndex, targetIndex);
            } else {
                if (!route?.route_uuid) throw new Error('Dragged route is missing its ID.');
                await onReassign(route, targetDriverName);
            }
        } catch (error) {
            logger.error('Route drop failed:', error);
            showAlert({ message: 'Could not drop the route or assignment. Please retry.', type: 'danger', buttons: [{ label: 'Close' }] });
        } finally {
            handleDragEnd();
        }
    };

    // ── Styles ────────────────────────────────────────────────────────────────

    const borderColor  = theme === 'dark' ? 'rgba(26,221,247,0.5)' : 'rgba(0,100,200,0.45)';
    const confirmedBorder = theme === 'dark' ? 'rgba(34,197,94,0.7)' : 'rgba(22,163,74,0.7)';
    const headerBg     = theme === 'dark' ? 'rgba(26,221,247,0.12)' : 'rgba(0,100,200,0.08)';
    const confirmedHeaderBg = theme === 'dark' ? 'rgba(34,197,94,0.22)' : 'rgba(34,197,94,0.18)';
    const headerColor  = theme === 'dark' ? 'rgba(26,221,247,0.9)' : 'rgba(0,80,180,0.9)';
    const confirmedHeaderColor = theme === 'dark' ? 'rgba(34,197,94,0.95)' : 'rgba(22,163,74,0.95)';
    const colCount = 9 + (showCustomerConsignment ? 1 : 0) + (showNetworkFreightMetrics ? 2 : 0) + (showCostings ? 3 : 0) + (showNetworkFreightMetrics && showCostings ? 1 : 0);

    if (!driverGroups || driverGroups.length === 0) return null;

    return (
        <div style={{ marginBottom: 18 }}>
            {/* Move-to-date overlay */}
            {moveUuid && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    onClick={() => setMoveUuid(null)}
                >
                    <div style={{ background: theme === 'dark' ? '#222' : '#fff', borderRadius: 10, padding: 24, minWidth: 280, boxShadow: '0 4px 20px rgba(0,0,0,0.4)' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h4 style={{ margin: '0 0 16px', color: theme === 'dark' ? '#fff' : '#222' }}>Move job to date</h4>
                        <input type="date" value={moveDate} onChange={(e) => setMoveDate(e.target.value)}
                            style={{ display: 'block', width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc', marginBottom: 16, fontSize: 15 }}
                        />
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                            <button onClick={() => setMoveUuid(null)} style={{ padding: '7px 18px', borderRadius: 6, border: '1px solid #ccc', background: 'transparent', cursor: 'pointer', color: theme === 'dark' ? '#ccc' : '#555' }}>Cancel</button>
                            <button onClick={() => { if (moveDate) { moveRoute(moveUuid, moveDate); setMoveUuid(null); setMoveDate(''); } }} disabled={!moveDate} style={{ padding: '7px 18px', borderRadius: 6, border: 'none', background: moveDate ? '#6366f1' : '#aaa', color: '#fff', cursor: moveDate ? 'pointer' : 'not-allowed' }}>Move</button>
                        </div>
                    </div>
                </div>
            )}
            <div id={theme} className={`route-container ${theme}`} style={{ display: 'block', padding: '10px', overflowX: 'auto' }}>
            <table
                className="route-table"
                style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}
            >
                <thead>
                    <tr>
                        <th className="route-order-cell" style={{ background: 'rgba(0,0,0,0.3)' }}>#</th>
                        <th className="fixed-th" style={{ background: 'rgba(0,0,0,0.3)' }}>
                            <FontAwesomeIcon style={{ width: 20, color: 'rgba(7,192,1,0.95)' }} icon={faCheck} />
                        </th>
                        <th className="fixed-th" style={{ background: 'rgba(0,0,0,0.3)' }}>
                            <FontAwesomeIcon style={{ width: 20, color: 'rgba(255,0,0,1)' }} icon={faStopwatch} />
                        </th>
                        <th className="fixed-th" style={{ background: 'rgba(0,0,0,0.3)' }}>
                            <FontAwesomeIcon style={{ width: 20, color: 'rgba(26,157,215,0.95)' }} icon={faClipboardList} />
                        </th>
                        <th style={{ background: 'rgba(0,0,0,0.3)' }}>Customer</th>
                        {showCustomerConsignment && <th style={{ background: 'rgba(0,0,0,0.3)' }}>Customer Ref</th>}
                        {showNetworkFreightMetrics && <th style={{ background: 'rgba(0,0,0,0.3)' }}>Pallets</th>}
                        {showNetworkFreightMetrics && <th style={{ background: 'rgba(0,0,0,0.3)' }}>Weight</th>}
                        {showNetworkFreightMetrics && showCostings && <th style={{ background: 'rgba(0,0,0,0.3)' }}>ADR</th>}
                        <th style={{ background: 'rgba(0,0,0,0.3)' }}>Route</th>
                        <th style={{ background: 'rgba(0,0,0,0.3)' }}>Job Comments</th>
                        <th style={{ background: 'rgba(0,0,0,0.3)' }}>Vehicle</th>
                        <th style={{ background: 'rgba(0,0,0,0.3)' }}>Trailer</th>
                        {showCostings && <th style={{ background: 'rgba(0,0,0,0.3)' }}>Start</th>}
                        {showCostings && <th style={{ background: 'rgba(0,0,0,0.3)' }}>Consignment</th>}
                        {showCostings && <th style={{ background: 'rgba(0,0,0,0.3)' }}>Costings</th>}
                    </tr>
                </thead>

                {driverGroups.map((group) => {
                    const allConfirmed = group.routes.length > 0 && group.routes.every((r) => r.confirmed);
                    const activeBorder = allConfirmed ? confirmedBorder : borderColor;
                    const activeHeaderBg = allConfirmed ? confirmedHeaderBg : headerBg;
                    const activeHeaderColor = allConfirmed ? confirmedHeaderColor : headerColor;
                    const totalPallets = group.routes.reduce((sum, route) => sum + (Number(route.pallet_count) || 0), 0);
                    const totalWeight = group.routes.reduce((sum, route) => sum + (Number(route.weight) || 0), 0);
                    return (
                    <tbody
                        key={group.driverName}
                        style={{ border: `2px solid ${activeBorder}` }}
                    >
                        {/* ── Driver header row ── */}
                        <tr style={{ background: activeHeaderBg }}>
                            <td
                                colSpan={colCount}
                                style={{
                                    fontWeight: 700,
                                    fontSize: 14,
                                    padding: '7px 14px',
                                    color: activeHeaderColor,
                                    borderBottom: `1px solid ${activeBorder}`,
                                    userSelect: 'none',
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <span>
                                    {group.driverName} — {group.routes.length} drop{group.routes.length !== 1 ? 's' : ''}
                                    {allConfirmed && (
                                        <FontAwesomeIcon
                                            icon={faCheck}
                                            style={{ width: 14, marginLeft: 10, color: confirmedHeaderColor }}
                                            title="All routes confirmed"
                                        />
                                    )}
                                </span>
                                {canEdit && !isLocked && group.routes.length > 1 && (
                                    <span
                                        title="Optimise drop order using AI"
                                        style={{ cursor: optimiseModal.loading && optimiseModal.driverName === group.driverName ? 'wait' : 'pointer', opacity: 0.85, fontSize: 12, display: 'flex', alignItems: 'center', gap: 5 }}
                                        onClick={() => handleOptimiseClick(group)}
                                    >
                                        <FontAwesomeIcon icon={faRoute} style={{ width: 13 }} />
                                        {optimiseModal.loading && optimiseModal.driverName === group.driverName ? 'Optimising…' : 'Optimise'}
                                    </span>
                                )}
                                </div>
                            </td>
                        </tr>

                        {/* ── Route rows ── */}
                        {group.routes.map((route, index) => {
                            const {
                                job, route_uuid, truck_id, truck_reg,
                                trailer_id, trailer_reg, external_trailer,
                                job_comments, route: routeDetails, route_ref,
                                completed, confirmed, is_subbed, demurrage, demurrage_cost,
                                costings, start_time, drop_number, customer_company, customer_consignment_number,
                                pallet_count, weight, is_network_freight, adr_value,
                            } = route;

                            const isCompleted = !!completedRouteUuids?.[route_uuid];
                            const isDragOver  = dragOverKey === `${group.driverName}-${index}`;

                            const rowBg = isCompleted
                                ? index % 2 === 0 ? 'rgba(26,157,215,0.35)' : 'rgba(56,189,248,0.28)'
                                : isDragOver
                                    ? theme === 'dark' ? 'rgba(26,221,247,0.2)' : 'rgba(0,100,200,0.12)'
                                    : theme === 'dark' ? 'rgba(50,50,50,0.85)' : 'rgba(255,255,255,0.85)';

                            const rowColor = isCompleted ? (theme === 'dark' ? '#f8f9fa' : '#333') : theme === 'light' ? '#333' : '#ddd';

                            return (
                                <tr
                                    key={route_uuid}
                                    data-driver-drop-route={!isLocked && !is_subbed ? route_uuid : undefined}
                                    className={showNetworkFreightMetrics && is_network_freight && adr_value ? 'network-adr-row' : undefined}
                                    onDragEnter={!isLocked && !confirmed ? () => setDragOverKey(`${group.driverName}-${index}`) : undefined}
                                    onDrop={!isLocked ? (e) => handleDropOnRow(e, group.driverName, index, route) : undefined}
                                    onDragOver={!isLocked ? allowRouteDrop : undefined}
                                    style={{ background: rowBg, color: rowColor }}
                                >
                                    {/* # drop number + unassign button */}
                                    <td className="route-order-cell">
                                      <div className="route-order-controls">
                                        <span
                                            className="route-order-handle"
                                            title="Drag to reorder"
                                            draggable={!isLocked && !confirmed && !isReordering}
                                            onDragStart={!isLocked && !confirmed && !isReordering ? (e) => handleDragStart(e, group.driverName, index, route) : undefined}
                                            onDragEnd={!isLocked && !confirmed ? handleDragEnd : undefined}
                                            style={{
                                                userSelect: 'none',
                                                whiteSpace: 'nowrap',
                                                cursor: isLocked || confirmed ? 'default' : 'grab',
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faGripVertical} style={{ width: 8 }} />
                                            {(isLocked || confirmed || !onReorder) && (
                                                <span className="route-order-number">{drop_number ?? index + 1}</span>
                                            )}
                                        </span>
                                        {!isLocked && !confirmed && onReorder && (
                                            <span className="route-order-arrows">
                                                {[
                                                    { direction: 'up', offset: -1, icon: faChevronUp },
                                                    { direction: 'down', offset: 1, icon: faChevronDown },
                                                ].map(({ direction, offset, icon }) => (
                                                    <React.Fragment key={direction}>
                                                    {direction === 'down' && (
                                                        <span className="route-order-number">{drop_number ?? index + 1}</span>
                                                    )}
                                                    <button
                                                        key={direction}
                                                        type="button"
                                                        className="route-order-button"
                                                        aria-label={`Move ${job || customer_company || route_uuid} ${direction} for ${group.driverName}`}
                                                        title={`Move job ${direction}`}
                                                        disabled={isReordering || index + offset < 0 || index + offset >= group.routes.length}
                                                        draggable={false}
                                                        onPointerDown={(e) => e.stopPropagation()}
                                                        onDragStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            void reorderWithinDriver(group, index, index + offset);
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={icon} style={{ width: 10 }} />
                                                    </button>
                                                    </React.Fragment>
                                                ))}
                                            </span>
                                        )}
                                        {!isLocked && !confirmed && onRemoveDriver && (
                                            <button
                                                type="button"
                                                className="route-order-unassign"
                                                aria-label={`Unassign ${job || customer_company || route_uuid}`}
                                                title="Move back to unallocated"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    showAlert({
                                                        message: `Move "${job}" back to unallocated?`,
                                                        type: 'warning',
                                                        buttons: [
                                                            { label: 'Unassign', type: 'warning', onClick: () => onRemoveDriver(route_uuid) },
                                                            { label: 'Cancel', type: 'secondary' },
                                                        ],
                                                    });
                                                }}
                                            >
                                            <FontAwesomeIcon
                                                icon={faArrowTurnUp}
                                                style={{
                                                    width: 11,
                                                    fontSize: 11,
                                                }}
                                            />
                                            </button>
                                        )}
                                      </div>
                                    </td>

                                    {/* Status — left-click confirms all for driver (or menu for subbed); right-click for Sub/Pod menu */}
                                    <td
                                        className="fixed-th"
                                        title={isCompleted ? 'View Completion (right-click to Sub)' : is_subbed ? 'Subcontracted (click for options)' : 'Confirm all for this driver (right-click for more options)'}
                                        style={{ width: 40, cursor: isCompleted ? 'pointer' : isLocked ? 'not-allowed' : 'crosshair' }}
                                        onClick={() => {
                                            if (isCompleted) {
                                                onViewCompletion(route_uuid, { driver: group.driverName, truck_reg, trailer_reg, job_comments, route_ref });
                                            } else if (!isLocked) {
                                                if (is_subbed) {
                                                    handleToggleConfirmed(route_uuid, confirmed, is_subbed, group.routes);
                                                } else if (onConfirmAllForDriver) {
                                                    onConfirmAllForDriver(group.routes);
                                                }
                                            }
                                        }}
                                        onContextMenu={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (isCompleted) {
                                                setPodContextMenu({ x: e.clientX, y: e.clientY, route_uuid });
                                            } else if (!isLocked) {
                                                handleToggleConfirmed(route_uuid, confirmed, is_subbed, group.routes);
                                            }
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            icon={is_subbed ? faTruckArrowRight : faEye}
                                            style={{
                                                width: 20,
                                                color: isCompleted
                                                    ? theme === 'dark' ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)'
                                                    : is_subbed
                                                    ? 'rgba(0,0,0,0.9)'
                                                    : theme === 'dark' ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)',
                                            }}
                                        />
                                    </td>

                                    {/* Demurrage */}
                                    <td
                                        className="fixed-th"
                                        style={{ width: 40, cursor: isLocked ? 'not-allowed' : 'pointer' }}
                                        title={demurrage ? `${demurrage}: £${demurrage_cost}` : 'Add Demurrage'}
                                        onClick={() => { if (!isLocked) showDemurrage(route_uuid); }}
                                    >
                                        <FontAwesomeIcon
                                            icon={faStopwatch}
                                            style={{ width: 20, color: demurrage ? '#ff0000' : theme === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}
                                        />
                                    </td>

                                    {/* Delivery details */}
                                    <td
                                        className="fixed-th"
                                        style={{ width: 40, cursor: 'pointer' }}
                                        title="View / Edit Delivery Details"
                                        onClick={() => showDeliveryDetails(route_uuid)}
                                    >
                                        <FontAwesomeIcon
                                            icon={faClipboardList}
                                            style={{ width: 20, color: deliveryAlerts?.has(route_uuid) ? 'rgba(220,53,69,0.95)' : theme === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}
                                        />
                                    </td>

                                    {/* Customer — click shows confirmation before delete */}
                                    <td className={!isLocked ? 'cell-hoverable' : ''}>
                                        <span
                                            style={{ cursor: isLocked ? 'default' : 'pointer' }}
                                            onClick={() => { if (!isLocked) confirmDelete(route_uuid); }}
                                        >{customer_company || '—'}</span>
                                    </td>

                                    {showCustomerConsignment && <td>{customer_consignment_number || '----'}</td>}
                                    {showNetworkFreightMetrics && <td>{pallet_count ?? '----'}</td>}
                                    {showNetworkFreightMetrics && <td>{weight == null ? '----' : `${Number(weight).toLocaleString('en-GB', { maximumFractionDigits: 2 })} kg`}</td>}
                                    {showNetworkFreightMetrics && showCostings && <td>{is_network_freight ? adr_value || '----' : '----'}</td>}

                                    {/* Route */}
                                    <td>{routeDetails || 'No route details'}</td>

                                    {/* Job Comments */}
                                    <td
                                        className={!isLocked ? 'cell-hoverable' : ''}
                                        style={{ maxWidth: 500, whiteSpace: 'normal', wordWrap: 'break-word' }}
                                    >
                                        <span
                                            style={{ cursor: isLocked ? 'default' : 'pointer' }}
                                            onClick={() => { if (!isLocked) showJobComments(route_uuid, job_comments); }}
                                        >{job_comments || <span style={{ opacity: 0.35 }}>----</span>}</span>
                                    </td>

                                    {/* Vehicle */}
                                    {truck_reg ? (
                                        <td
                                            className={!isLocked ? 'cell-hoverable' : ''}
                                            style={{
                                                backgroundColor: !vans.includes(truck_reg) && vehicleServiceList?.includes(truck_reg)
                                                    ? 'rgba(253,191,58,0.6)'
                                                    : vehicleOverdueList?.includes(truck_reg)
                                                        ? 'rgba(253,69,58,0.6)'
                                                        : 'none',
                                            }}
                                        >
                                            <span
                                                style={{ cursor: isLocked ? 'default' : 'pointer' }}
                                                onClick={() => { if (!isLocked) removeVehicle(route_uuid, 'truck'); }}
                                            >
                                                {truck_reg}{vorList?.includes(truck_reg) && <strong> (VOR)</strong>}
                                            </span>
                                        </td>
                                    ) : (
                                        <td className="cell-hoverable">
                                            <span
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => { if (!isLocked) showAddFromDropdown(route_uuid, 'truck'); }}
                                            >----</span>
                                        </td>
                                    )}

                                    {/* Trailer */}
                                    {trailer_reg ? (
                                        <td
                                            className={!isLocked ? 'cell-hoverable' : ''}
                                            style={{
                                                backgroundColor: vehicleServiceList?.includes(trailer_reg)
                                                    ? 'rgba(253,191,58,0.6)'
                                                    : vehicleOverdueList?.includes(trailer_reg)
                                                        ? 'rgba(253,69,58,0.6)'
                                                        : 'none',
                                            }}
                                        >
                                            <span
                                                style={{ cursor: isLocked ? 'default' : 'pointer' }}
                                                onClick={() => { if (!isLocked) removeVehicle(route_uuid, 'trailer'); }}
                                            >{trailer_id === 1 ? external_trailer : trailer_reg}</span>
                                        </td>
                                    ) : (
                                        <td className="cell-hoverable">
                                            <span
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => { if (!isLocked) showAddFromDropdown(route_uuid, 'trailer'); }}
                                            >----</span>
                                        </td>
                                    )}

                                    {showCostings && (
                                        <td className="cell-hoverable">
                                            <span
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => showStartTime(route_uuid, start_time)}
                                            >{formatTimeToHHMM(start_time) || '----'}</span>
                                        </td>
                                    )}
                                    {showCostings && <td>{route_ref || '----'}</td>}
                                    {showCostings && (
                                        <td className="cell-hoverable">
                                            <span
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => showJobCostings(route_uuid, costings)}
                                            >{costings ? '£' + costings : '----'}</span>
                                        </td>
                                    )}
                                </tr>
                            );
                        })};
                        {showNetworkFreightMetrics && (
                            <tr style={{ background: activeHeaderBg, color: activeHeaderColor, fontWeight: 700 }}>
                                <td colSpan={5 + (showCustomerConsignment ? 1 : 0)} style={{ textAlign: 'right' }}>
                                    {group.driverName} total
                                </td>
                                <td>{totalPallets.toLocaleString('en-GB')}</td>
                                <td>{totalWeight.toLocaleString('en-GB', { maximumFractionDigits: 2 })} kg</td>
                                <td colSpan={4 + (showCostings ? 3 : 0) + (showNetworkFreightMetrics && showCostings ? 1 : 0)} />
                            </tr>
                        )}
                        {/* Transparent spacer between driver groups */}
                        <tr style={{ height: 4 }}><td colSpan={colCount} style={{ background: 'transparent', padding: 0, border: 'none' }} /></tr>
                    </tbody>
                    );
                })}
            </table>
            </div>

            {/* Right-click context menu for podded routes — allows subcontracting after pod */}
            {podContextMenu && (
                <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        position: 'fixed',
                        top: podContextMenu.y,
                        left: podContextMenu.x,
                        zIndex: 9999,
                        background: '#fff',
                        border: '1px solid #ccc',
                        borderRadius: 6,
                        boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
                        minWidth: 140,
                        padding: '4px 0',
                    }}
                >
                    <div style={{ padding: '4px 12px 6px', fontSize: 11, color: '#888', borderBottom: '1px solid #eee', userSelect: 'none' }}>
                        Podded Route
                    </div>
                    <div
                        style={{ padding: '8px 16px', cursor: 'pointer', fontWeight: 600, color: '#0d6efd' }}
                        onClick={() => {
                            setPodContextMenu(null);
                            contractRoute(podContextMenu.route_uuid, true);
                        }}
                    >
                        Sub
                    </div>
                    <div
                        style={{ padding: '8px 16px', cursor: 'pointer', color: '#555' }}
                        onClick={() => setPodContextMenu(null)}
                    >
                        Cancel
                    </div>
                </div>
            )}

            <RouteOptimiseModal
                theme={theme}
                isOpen={optimiseModal.open || optimiseModal.loading}
                onClose={() => setOptimiseModal({ open: false, result: null, driverName: '', loading: false })}
                onAccept={handleAcceptOptimise}
                optimised={optimiseModal.result}
                driverName={optimiseModal.driverName}
            />
        </div>
    );
};

export default AllocatedTable;
